<div class="verification-container p-4">
  <div class="page-header d-flex justify-content-between align-items-center mb-4">
    <div>
      <h2>Profile Verification Queue</h2>
      <p>Verify and manage identification documents submitted during registration.</p>
    </div>
    <button (click)="loadQueue()" class="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 border-0" style="color: var(--color-primary, #B01E3E);">
      <span class="material-symbols-outlined text-sm">refresh</span> Refresh Queue
    </button>
  </div>

  <div *ngIf="loading" class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <div *ngIf="!loading && queue.length === 0" class="text-center py-5 bg-white rounded-3xl border border-gray-100 shadow-2xs">
    <span class="material-symbols-outlined text-[48px] text-gray-300">verified_user</span>
    <p class="text-muted text-sm mt-2">No verification requests found in the queue.</p>
  </div>

  <div *ngIf="!loading && queue.length > 0" class="users-table-wrap">
    <table class="users-table">
      <thead>
        <tr>
          <th>Name / Info</th>
          <th>Gender</th>
          <th>Age</th>
          <th>Verification Status</th>
          <th>Uploaded Date</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let user of queue">
          <td class="td-profile">
            <div class="profile-thumb">
              <img *ngIf="base64Images[user._id]" [src]="base64Images[user._id]" alt="Avatar">
              <span *ngIf="!base64Images[user._id]">{{ getInitials(user) }}</span>
            </div>
            <div>
              <div class="user-name">{{ user.basic_info?.first_name }} {{ user.basic_info?.last_name }}</div>
              <div class="user-email">{{ user.basic_info?.email }} | {{ user.basic_info?.mobile }}</div>
            </div>
          </td>
          <td>
            <span class="gender-badge" [class]="user.basic_info?.gender?.toLowerCase() || ''">
              {{ user.basic_info?.gender || '—' }}
            </span>
          </td>
          <td>
            <span class="font-semibold text-secondary">{{ getAge(user.basic_info?.date_of_birth) }} Yrs</span>
          </td>
          <td>
            <span class="status-badge" [class]="(user.verification_status || 'unverified') | lowercase">{{ user.verification_status || 'UNVERIFIED' }}</span>
          </td>
          <td>
            <div class="text-xs font-mono text-muted">{{ user.aadhaar_uploaded_at | date:'mediumDate' }}</div>
          </td>
          <td>
            <button (click)="viewAadhaar(user)" class="btn-review">
              <span class="material-symbols-outlined text-[15px]">rate_review</span> Review
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

<!-- Aadhaar Preview Modal Overlay -->
<div *ngIf="selectedAadhaarUrl" class="preview-modal-backdrop">
  <div class="preview-modal-card">
    <div class="modal-header-custom">
      <h3>
        <span class="material-symbols-outlined text-rose-600 text-[20px]">badge</span>
        Review Aadhaar Identity Verification
      </h3>
      <button (click)="closePreview()" class="btn-close-modal">
        <span class="material-symbols-outlined">close</span>
      </button>
    </div>

    <div class="modal-body-custom">
      <!-- Left: Aadhaar Preview with Zoom and Download -->
      <div class="modal-left-pane">
        <div class="preview-container">
          <ng-container *ngIf="selectedUser?.aadhaar_file_name?.toLowerCase()?.endsWith('.pdf'); else imagePreview">
            <iframe [src]="selectedAadhaarUrl" style="width: 100%; height: 380px; border: 0;"></iframe>
          </ng-container>
          <ng-template #imagePreview>
            <img *ngIf="!imageError" [src]="selectedAadhaarUrl" (error)="onImageError()" [style.transform]="'scale(' + zoomLevel + ')'" alt="Aadhaar Front Preview">
            <div *ngIf="imageError" class="error-placeholder">
              <span class="material-symbols-outlined">broken_image</span>
              <span>Unable to load document. Please check with user or request re-upload.</span>
            </div>
          </ng-template>
        </div>
        
        <!-- Interactive Zoom & Download Controls -->
        <div class="preview-controls" *ngIf="!selectedUser?.aadhaar_file_name?.toLowerCase()?.endsWith('.pdf')">
          <button (click)="zoomIn()" title="Zoom In">
            <span class="material-symbols-outlined text-sm">zoom_in</span> Zoom In
          </button>
          <button (click)="zoomOut()" title="Zoom Out">
            <span class="material-symbols-outlined text-sm">zoom_out</span> Zoom Out
          </button>
          <button (click)="resetZoom()" title="Reset Zoom">
            <span class="material-symbols-outlined text-sm">restart_alt</span> Reset
          </button>
          <a [href]="selectedAadhaarUrl" download="Aadhaar_Document" target="_blank" title="Download Document">
            <span class="material-symbols-outlined text-sm">download</span> Download
          </a>
        </div>
        <div class="preview-controls" *ngIf="selectedUser?.aadhaar_file_name?.toLowerCase()?.endsWith('.pdf')">
          <a [href]="selectedAadhaarUrl" download="Aadhaar_Document.pdf" target="_blank" title="Download Document">
            <span class="material-symbols-outlined text-sm">download</span> Download PDF
          </a>
        </div>
      </div>

      <!-- Right: Detailed User Profile Summary -->
      <div class="modal-right-pane">
        <div class="user-profile-summary">
          <div class="user-avatar" [class.clickable-avatar]="base64Images[selectedUser._id]" (click)="base64Images[selectedUser._id] && openPhotoLightbox(selectedUser._id)" [title]="base64Images[selectedUser._id] ? 'Click to view full photo' : ''">
            <img *ngIf="base64Images[selectedUser._id]" [src]="base64Images[selectedUser._id]" alt="User Dp">
            <span *ngIf="!base64Images[selectedUser._id]">{{ getInitials(selectedUser) }}</span>
            <div *ngIf="base64Images[selectedUser._id]" class="avatar-expand-hint">
              <span class="material-symbols-outlined">open_in_full</span>
            </div>
          </div>
          <div class="user-info-text">
            <h4>{{ selectedUser?.basic_info?.first_name }} {{ selectedUser?.basic_info?.last_name }}</h4>
            <p>Verification Status: <strong class="text-rose-700">{{ selectedUser?.verification_status || 'UNVERIFIED' }}</strong></p>
          </div>
        </div>

        <div class="detail-grid">
          <div class="detail-section-title">Basic & Contact Info</div>
          <div class="detail-item">
            <label>Gender / Age</label>
            <span>{{ selectedUser?.basic_info?.gender || '—' }} / {{ getAge(selectedUser?.basic_info?.date_of_birth) }} Years</span>
          </div>
          <div class="detail-item">
            <label>Date of birth</label>
            <span>{{ selectedUser?.basic_info?.date_of_birth | date:'mediumDate' }}</span>
          </div>
          <div class="detail-item">
            <label>Marital Status</label>
            <span>{{ selectedUser?.personal_info?.marital_status || '—' }}</span>
          </div>
          <div class="detail-item">
            <label>Mobile Number</label>
            <span>{{ selectedUser?.basic_info?.mobile || '—' }}</span>
          </div>
          <div style="grid-column: span 2;" class="detail-item">
            <label>Email ID</label>
            <span>{{ selectedUser?.basic_info?.email || '—' }}</span>
          </div>
          <div style="grid-column: span 2;" class="detail-item">
            <label>Residing Location</label>
            <span>{{ getLocation(selectedUser) }}</span>
          </div>

          <div class="detail-section-title">Religious & Horoscope Info</div>
          <div class="detail-item">
            <label>Religion</label>
            <span>{{ selectedUser?.religion_info?.religion_id?.religion_nme || '—' }}</span>
          </div>
          <div class="detail-item">
            <label>Caste / Sub-Caste</label>
            <span>{{ getCaste(selectedUser) }} / {{ getSubCaste(selectedUser) }}</span>
          </div>
          <div class="detail-item">
            <label>Rasi / Nakshatra</label>
            <span>{{ getRasi(selectedUser) }} / {{ getNakshatra(selectedUser) }}</span>
          </div>
          <div class="detail-item">
            <label>Gothram / Dosham</label>
            <span>{{ getGothram(selectedUser) }}</span>
          </div>

          <div class="detail-section-title">Physical & Lifestyle Details</div>
          <div class="detail-item">
            <label>Height / Weight</label>
            <span>{{ selectedUser?.personal_info?.height ? selectedUser?.personal_info?.height + ' cm' : '—' }} / {{ selectedUser?.personal_info?.weight ? selectedUser?.personal_info?.weight + ' kg' : '—' }}</span>
          </div>
          <div class="detail-item">
            <label>Physical Status</label>
            <span>{{ selectedUser?.personal_info?.physical_status || '—' }}</span>
          </div>
          <div style="grid-column: span 2;" class="detail-item">
            <label>Diet / Drinking / Smoking</label>
            <span>{{ selectedUser?.personal_info?.food || '—' }} / {{ selectedUser?.personal_info?.drinking || 'No' }} / {{ selectedUser?.personal_info?.smoking || 'No' }}</span>
          </div>

          <div class="detail-section-title">Education & Career Info</div>
          <div style="grid-column: span 2;" class="detail-item">
            <label>Education</label>
            <span>{{ getEducation(selectedUser) }}</span>
          </div>
          <div style="grid-column: span 2;" class="detail-item">
            <label>Occupation</label>
            <span>{{ getOccupation(selectedUser) }}</span>
          </div>
          <div class="detail-item">
            <label>Annual Salary</label>
            <span>{{ getIncome(selectedUser) }}</span>
          </div>

          <div class="detail-section-title">Family Details</div>
          <div class="detail-item">
            <label>Family Type / Status</label>
            <span>{{ selectedUser?.family_info?.family_type || '—' }} / {{ selectedUser?.family_info?.family_status || '—' }}</span>
          </div>
          <div class="detail-item">
            <label>Brothers / Sisters</label>
            <span>{{ selectedUser?.family_info?.no_of_brothers || 0 }} / {{ selectedUser?.family_info?.no_of_sisters || 0 }}</span>
          </div>

          <div class="detail-section-title">Audit Timestamps</div>
          <div style="grid-column: span 2;" class="detail-item">
            <label>Profile Registered On</label>
            <span>{{ selectedUser?.profile_ctd_dt | date:'medium' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer-custom">
      <div class="status-info">
        Submitted: {{ selectedUser?.aadhaar_uploaded_at | date:'medium' }}
      </div>
      <div class="footer-btns">
        <button (click)="requestReupload(selectedUser._id)" class="btn-reupload">
          <span class="material-symbols-outlined text-[16px]">sync</span> Request Re-upload
        </button>
        <button (click)="reject(selectedUser._id)" class="btn-reject">
          <span class="material-symbols-outlined text-[16px]">close</span> Reject
        </button>
        <button (click)="approve(selectedUser._id)" class="btn-approve">
          <span class="material-symbols-outlined text-[16px]">check</span> Approve Verification
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Photo Lightbox Overlay -->
<div *ngIf="photoLightboxUrl" class="photo-lightbox-backdrop" (click)="closePhotoLightbox()">
  <div class="photo-lightbox-inner" (click)="$event.stopPropagation()">
    <button class="lightbox-close-btn" (click)="closePhotoLightbox()" title="Close">
      <span class="material-symbols-outlined">close</span>
    </button>
    <img [src]="photoLightboxUrl" alt="Full Profile Photo" class="lightbox-photo">
    <p class="lightbox-hint">Click anywhere outside or press <kbd>×</kbd> to close</p>
  </div>
</div>